import * as THREE from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import {
  AURAS,
  BASE_HP,
  CLASSES,
  CollisionWorld,
  DT,
  EMOTES,
  FREE_CONTROL,
  HUB_NPCS,
  KEYBOARD_TURN_RATE,
  NPC_RANGE,
  TILE_SIZE,
  UNIT_RADIUS,
  abilityDef,
  auraDef,
  cancelableAura,
  costOf,
  rangeOf,
  buildMapColliders,
  initPhysics,
  isAbilityId,
  moveStep,
  spellbook,
  type AbilityId,
  type AuraId,
  type ClassPair,
  type ClientMessage,
  type CombatEvent,
  type HubUnit,
  type MoveControl,
  type MoveInput,
  type MoveState,
  type School,
  type SelfSnap,
  type ServerMessage,
  type Snapshot,
  type UnitSnap,
  type V3,
} from '@arena/shared';
import {
  buildMapInstances,
  loadCollisionGeometry,
  loadModel,
  loadPieceTemplates,
  mapPieceIds,
} from '../lib/assets';
import { Environment, createRenderer, pixelRatio } from '../lib/environment';
import { chord } from '../lib/keys';
import {
  app,
  onMessage,
  send,
  sendMatch,
  type MatchInfo,
} from '../lib/net.svelte';
import {
  barFor,
  boundTo,
  keyLabel,
  settings,
  type CombatTextKind,
  talentsFor,
  utilityBar,
} from '../lib/settings.svelte';
import { STEPS } from '../lib/tutorial';
import { CameraRig } from './CameraRig';
import { Critters, PETS } from './Critters';
import {
  ARROW_SPEED,
  FX_MODELS,
  SCHOOL_COLORS,
  SpellFx,
  TOTEMS,
  areaDecal,
  areaRadius,
  disposeObject,
  fxShaders,
} from './Fx';
import {
  hud,
  togglePanel,
  type ActionView,
  type AuraView,
  type FrameView,
} from './hud.svelte';
import { lossOfControl } from './lossOfControl';
import { Meter, meterRows } from './meter';
import { Sfx, click, music } from './Sfx';
import { surfaceOf, type Footfall, type Surface } from './sounds';
import {
  CLASS_LOOKS,
  FORM_MODELS,
  NO_HANDS,
  TOTEM_MODEL,
  UnitView,
  castAnimation,
  unitShaders,
  type HeldModels,
} from './UnitView';

/** Remote units render this far behind the newest snapshot so there's always a pair to blend. */
const INTERP_DELAY = 0.1;
/** The hub broadcasts at 10 Hz, so it needs a deeper buffer. */
const HUB_INTERP_DELAY = 0.25;
const HUD_INTERVAL = 0.05;
/** Corrections larger than this teleport instead of smoothing (blink, charge, deaths). */
const SNAP_DISTANCE = 3;
const CORRECTION_RATE = 12;
const MAX_STEPS_PER_FRAME = 5;
const SAMPLE_WINDOW = 1;
const NAMEPLATE_HEIGHT = 1.35;
const GATE_OPEN_SECONDS = 1.5;
const CAMERA_FOLLOW_RATE = 5;
/** Winners nod this long once a match ends; losers kneel until they leave. */
const VICTORY_NOD_SECONDS = 3;

interface Sample {
  t: number;
  pos: THREE.Vector3;
  yaw: number;
  vel: THREE.Vector3;
  grounded: boolean;
}

interface Fx {
  object: THREE.Object3D;
  start: number;
  end: number;
  update: (progress: number) => void;
}

const toVector = (v: V3 | [number, number, number]) =>
  Array.isArray(v)
    ? new THREE.Vector3(v[0], v[1], v[2])
    : new THREE.Vector3(v.x, v.y, v.z);

const lerpAngle = (a: number, b: number, t: number) => {
  const diff = Math.atan2(Math.sin(b - a), Math.cos(b - a));
  return a + diff * t;
};

/** Fills a hub unit (or an opponent not seen yet) out into a full-health snapshot unit. */
const freshUnit = ({
  npc,
  ...unit
}: Omit<HubUnit, 'classId'> & { classes: ClassPair }): UnitSnap => ({
  ...unit,
  active: 0,
  team: 0,
  dummy: npc ?? false,
  hp: BASE_HP,
  maxHp: BASE_HP,
  resources: [
    CLASSES[unit.classes[0]].startResource,
    CLASSES[unit.classes[1]].startResource,
  ],
  targetId: null,
  cast: null,
  auras: [],
  dead: false,
  stealthed: false,
});

export class Game {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(60, 1, 0.1, 400);
  private rig = new CameraRig(this.camera);
  /** Starts with the gates shut; swapped for `openWorld` once the match goes live. */
  private world: CollisionWorld | null = null;
  private openWorld: CollisionWorld | null = null;
  private gates: THREE.Object3D[] = [];
  private mapObject: THREE.Object3D | null = null;
  private critters: Critters | null = null;
  private environment: Environment | null = null;
  private characters: Record<string, GLTF> = {};
  private items: HeldModels = {};
  private views = new Map<string, UnitView>();
  private samples = new Map<string, Sample[]>();
  private latest: Snapshot | null = null;
  private latestAt = 0;
  /** Live floating combat text, re-placed over its unit every frame. */
  private floaters: {
    el: HTMLElement;
    unitId: string;
    anchor: THREE.Vector3;
    jitter: number;
  }[] = [];
  private hubSelf: SelfSnap | null = null;
  /** Each unit's match-end reaction clip and when it stops, in seconds of `performance.now()`. */
  private reactions = new Map<string, { clip: string; until: number }>();
  private clockOffset: number | null = null;
  private knownEnemies = new Map<string, UnitSnap>();
  private lockoutDurations: Partial<Record<School, number>> = {};
  private meter = new Meter();
  private myTeam: 0 | 1 = 0;

  private predicted: MoveState | null = null;
  private previous: MoveState | null = null;
  private pending: MoveInput[] = [];
  private seq = 0;
  /** Our last input that moved; the server drops our emote on it, so until it's acked the emote is stale. */
  private movedSeq = 0;
  private control: MoveControl = FREE_CONTROL;
  private accumulator = 0;
  private visualOffset = new THREE.Vector3();
  private charYaw = 0;

  private keys = new Set<string>();
  private moving = false;
  private mouse = {
    left: false,
    right: false,
    dragging: false,
    downX: 0,
    downY: 0,
  };
  /** The cursor over the canvas while it isn't dragging the camera, for critter nameplates and the hover cursor. */
  private hoverAt: { x: number; y: number } | null = null;
  private targetId: string | null = null;
  /** A ground ability waiting for a left click on where it lands; right click or Escape cancels. */
  private placing: { abilityId: AbilityId; reticle: THREE.Group } | null = null;
  private tabCycle: string[] = [];
  private raycaster = new THREE.Raycaster();
  /** Straight down from just above a unit's feet, to find what it stands on. */
  private ground = new THREE.Raycaster(
    new THREE.Vector3(),
    new THREE.Vector3(0, -1, 0),
    0,
    1,
  );
  private effects: Fx[] = [];
  private fx = new SpellFx(this.scene, (id) => this.views.get(id));
  private sfx = new Sfx();
  private hudTimer = 0;
  private lastFrame = performance.now() / 1000;
  /** When the frame cap next lets a frame draw, in ms. */
  private nextDraw = 0;
  private renderScale = settings.renderScale;
  private fps = { frames: 0, since: performance.now() };
  private running = true;
  private disposers: (() => void)[] = [];

  constructor(
    private canvas: HTMLCanvasElement,
    private overlay: HTMLElement,
    private match: MatchInfo,
  ) {
    this.renderer = createRenderer(canvas, settings);
    this.disposers.push(onMessage((message) => this.onMessage(message)));
  }

  async start(): Promise<void> {
    const hub = this.match.bracket === 'hub';
    const looks = Object.values(CLASS_LOOKS);
    const characters = looks
      .map((look) => look.character)
      .concat(FORM_MODELS)
      .concat(hub ? HUB_NPCS.map((npc) => npc.model) : [TOTEM_MODEL]);
    const items = looks
      .flatMap((look) => [look.hands.right, look.hands.left])
      .filter((item) => item !== null)
      .concat(FX_MODELS);
    hud.loading = { done: 0, total: 0 };
    const track = <T>(promise: Promise<T>): Promise<T> => {
      hud.loading.total++;
      return promise.then((value) => {
        hud.loading.done++;
        return value;
      });
    };
    const load = (name: string) => track(loadModel(name));
    const [, templates, geometry, characterModels, itemModels, pets] =
      await Promise.all([
        track(initPhysics()),
        loadPieceTemplates(mapPieceIds(this.match.map), track),
        loadCollisionGeometry(mapPieceIds(this.match.map), track),
        Promise.all(characters.map(load)),
        Promise.all(items.map(load)),
        Promise.all((hub ? PETS : []).map((pet) => load(pet.model))),
      ]);
    if (!this.running) return;
    this.world = new CollisionWorld(
      buildMapColliders(this.match.map, geometry, true),
    );
    this.openWorld = new CollisionWorld(
      buildMapColliders(this.match.map, geometry),
    );
    if (hub) {
      this.critters = new Critters(
        this.scene,
        this.openWorld,
        pets,
        this.overlay,
      );
      this.critters.onFootfall = (at) =>
        this.sfx.patter(at, this.surfaceAt(at.position));
    }
    this.characters = Object.fromEntries(
      characters.map((name, i) => [name, characterModels[i]]),
    );
    this.items = Object.fromEntries(
      items.map((name, i) => [name, itemModels[i].scene]),
    );
    this.fx.models = this.items;
    const { object, gates } = buildMapInstances(this.match.map, templates);
    this.gates = gates;
    this.mapObject = object;
    this.scene.add(object, this.sfx.listener);
    this.environment = new Environment(
      this.renderer,
      this.scene,
      this.match.map,
      hub,
    );
    // Never disposed: three deletes a shader once no material uses it, and compiling one mid-fight stalls frames (badly on Windows).
    this.renderer.compile(
      new THREE.Group().add(
        unitShaders(Object.values(this.characters)),
        fxShaders(),
      ),
      this.camera,
      this.scene,
    );
    this.bindInput();
    const resize = new ResizeObserver(() => this.resize());
    resize.observe(this.canvas.parentElement!);
    this.disposers.push(() => resize.disconnect());
    this.resize();
    hud.ready = true;
    requestAnimationFrame(this.frame);
  }

  dispose(): void {
    this.running = false;
    for (const dispose of this.disposers) dispose();
    this.fx.dispose();
    this.sfx.dispose();
    music(null);
    this.critters?.dispose();
    for (const view of this.views.values()) view.dispose();
    this.views.clear();
    this.renderer.dispose();
    this.world?.free();
    this.openWorld?.free();
    hud.ready = false;
    hud.npc = null;
    hud.panel = null;
    if (document.pointerLockElement) document.exitPointerLock();
  }

  // -------------------------------------------------------------------------------------------
  // Network
  // -------------------------------------------------------------------------------------------

  private onMessage(message: ServerMessage): void {
    if (message.t === 'chat') {
      const { fromId, text, channel } = message.line;
      if (channel === 'system' || channel === 'whisper') return;
      return void this.views.get(fromId ?? '')?.say(text, channel);
    }
    if (!this.world) return;
    if (this.match.bracket !== 'hub') {
      if (message.t === 'matchEnd') return this.react(message.winner);
      if (message.t !== 'snap') return;
      const before = this.latest;
      this.onSnapshot(message.snap);
      return this.onEvents(message.events, before);
    }
    if (message.t === 'hubSelf') {
      this.hubSelf = {
        id: this.match.youId,
        ack: message.ack,
        move: message.move,
        control: FREE_CONTROL,
        combo: 0,
        gcd: { remaining: 0, duration: 0 },
        cooldowns: {},
        used: {},
        lockouts: {},
        queued: null,
        swing: null,
      };
    }
    if (message.t !== 'hub') return;
    this.onSnapshot({
      tick: message.tick,
      phase: 'live',
      phaseRemaining: 0,
      dampening: 0,
      // Hub units carry only the class they're drawn as; our own frame and bars show both of ours.
      units: message.units.map(({ classId, ...unit }) =>
        freshUnit({
          ...unit,
          classes:
            unit.id === this.match.youId ? app.classes : [classId, classId],
        }),
      ),
      zones: [],
      self: this.hubSelf,
    });
  }

  private react(winner: 0 | 1 | null): void {
    if (winner === null || !this.latest) return;
    const now = performance.now() / 1000;
    for (const unit of this.latest.units)
      this.reactions.set(
        unit.id,
        unit.team === winner
          ? { clip: 'emote-yes', until: now + VICTORY_NOD_SECONDS }
          : { clip: 'crouch', until: Infinity },
      );
  }

  /** Movement and combat go to whichever server runs this world. */
  private post(message: ClientMessage): void {
    if (this.match.bracket === 'hub') send(message);
    else sendMatch(message);
  }

  private onSnapshot(snap: Snapshot): void {
    if (snap.phase !== 'prep') this.openGates();
    const world = this.world!;
    const now = performance.now() / 1000;
    const serverTime = snap.tick * DT;
    // Each hub (and a restarted lobby) counts ticks from its own start, and the NPCs are in every hub.
    if (snap.tick < (this.latest?.tick ?? 0)) {
      this.clockOffset = null;
      this.samples.clear();
    }
    // The fastest-arriving snapshot best estimates the clock; let it drift down slowly.
    const offset = serverTime - now;
    this.clockOffset =
      this.clockOffset === null
        ? offset
        : Math.max(offset, this.clockOffset - 0.002);
    this.latest = snap;
    this.latestAt = now;

    const me = snap.units.find((u) => u.id === this.match.youId);
    if (me) this.myTeam = me.team;
    this.fx.zones(snap.zones, this.myTeam);
    const enemyTeam = this.myTeam === 0 ? 1 : 0;
    for (const f of app.roster?.[enemyTeam] ?? []) {
      if (this.knownEnemies.has(f.id)) continue;
      const unseen = freshUnit({
        ...f,
        pos: [0, 0, 0],
        yaw: 0,
        vel: [0, 0, 0],
        grounded: true,
      });
      this.knownEnemies.set(f.id, { ...unseen, team: enemyTeam });
    }
    const present = new Set<string>();
    const quest: string[] =
      settings.tutorial === 'done' ? [] : STEPS[settings.tutorial].npcs;
    for (const unit of snap.units) {
      present.add(unit.id);
      const view = this.viewFor(unit);
      view.root.visible = true;
      view.nameplate.style.visibility = 'visible';
      view.nameplate.classList.toggle(
        'quest',
        quest.includes(unit.id) && hud.npc !== unit.id,
      );
      if (unit.team !== this.myTeam && !unit.dummy && !unit.totem)
        this.knownEnemies.set(unit.id, unit);
      if (unit.id === this.match.youId) continue;
      const list = this.samples.get(unit.id) ?? [];
      list.push({
        t: serverTime,
        pos: toVector(unit.pos),
        yaw: unit.yaw,
        vel: toVector(unit.vel),
        grounded: unit.grounded,
      });
      while (list.length > 2 && list[0].t < serverTime - SAMPLE_WINDOW)
        list.shift();
      this.samples.set(unit.id, list);
    }
    for (const [id, view] of this.views) {
      if (present.has(id)) continue;
      // Only stealth and fallen totems drop a unit from a match snapshot.
      if (view.root.visible && this.match.bracket !== 'hub')
        this.fx.vanish(view);
      view.root.visible = false;
      view.nameplate.style.visibility = 'hidden';
      this.samples.delete(id);
      if (this.targetId === id) this.setTarget(null);
      if (!view.totem) continue;
      view.dispose();
      this.scene.remove(view.root);
      this.views.delete(id);
    }

    const self = snap.self;
    if (!self) return;
    this.control = self.control;
    this.pending = this.pending.filter((input) => input.seq > self.ack);
    let state = self.move;
    for (const input of this.pending)
      state = moveStep(world, state, input, this.control);
    if (this.predicted) {
      const error = toVector(this.predicted.pos).sub(toVector(state.pos));
      if (error.length() > SNAP_DISTANCE) {
        this.visualOffset.set(0, 0, 0);
        this.previous = state;
      } else {
        this.visualOffset.add(error);
        if (this.previous) {
          const p = this.previous.pos;
          this.previous = {
            ...this.previous,
            pos: { x: p.x - error.x, y: p.y - error.y, z: p.z - error.z },
          };
        }
      }
    } else {
      this.charYaw = state.yaw;
      this.rig.yaw = state.yaw;
      this.previous = state;
    }
    this.predicted = state;
  }

  resetMeter(): void {
    this.meter = new Meter();
  }

  /** `before`: the snapshot these events happened after, to tell a fresh aura from a refreshed one. */
  private onEvents(events: CombatEvent[], before: Snapshot | null): void {
    const me = this.match.youId;
    const root = (id: string | null) => this.views.get(id ?? '')?.root;
    const involved = (e: { source?: string | null; target: string }) =>
      e.source === me || e.target === me;
    const had = (unitId: string, auraId: AuraId) =>
      !!before?.units
        .find((u) => u.id === unitId)
        ?.auras.some((a) => a.auraId === auraId);
    const ticking = (auraId?: AuraId) => !!auraId && !!auraDef(auraId).periodic;
    this.meter.add(events, me, performance.now() / 1000);
    for (const event of events) {
      switch (event.type) {
        case 'damage': {
          const swinger =
            event.abilityId === 'melee' && event.source
              ? this.views.get(event.source)
              : undefined;
          const ranged = !!(swinger && CLASSES[swinger.classId].swing.range);
          const blocked = event.amount === 0 && event.absorbed > 0;
          swinger?.playOnce(
            ranged
              ? 'holding-right-shoot'
              : Math.random() < 0.5
                ? 'attack-melee-right'
                : 'attack-melee-left',
          );
          if (swinger) {
            this.fx.melee(swinger.id, event.target, ranged);
            const target = root(event.target);
            const flight = target
              ? swinger.root.position.distanceTo(target.position) / ARROW_SPEED
              : 0;
            this.sfx.swing(swinger.root, target, ranged, blocked, flight);
          } else if (event.abilityId === null)
            this.fx.periodic(event.target, event.school);
          else if (event.source && event.abilityId !== 'melee') {
            this.fx.channelTick(event.abilityId, event.source, event.target);
            if (event.source !== event.target)
              this.sfx.impact(
                event.target,
                root(event.target),
                event.abilityId,
                blocked,
              );
          }
          if (involved(event)) {
            const taken = event.target === me;
            const text = event.amount > 0 ? `${event.amount}` : 'Absorb';
            const tinted = !taken && event.school !== 'physical';
            this.combatText(
              event.target,
              text +
                (event.absorbed > 0 && event.amount > 0
                  ? ` (${event.absorbed} absorbed)`
                  : ''),
              taken ? 'taken' : 'dealt',
              taken ? 'taken' : event.abilityId === 'melee' ? 'melee' : 'spell',
              {
                periodic: ticking(event.auraId),
                color: tinted
                  ? `#${SCHOOL_COLORS[event.school].toString(16).padStart(6, '0')}`
                  : undefined,
              },
            );
          }
          break;
        }
        case 'absorb':
          if (event.source === me && event.target !== me)
            this.combatText(
              event.target,
              `${event.amount} absorbed`,
              'absorbs',
              'absorb',
            );
          break;
        case 'heal':
          if (event.abilityId === null) this.fx.periodic(event.target, null);
          else if (event.source) {
            this.fx.channelTick(event.abilityId, event.source, event.target);
            if (event.source !== event.target)
              this.sfx.impact(
                event.target,
                root(event.target),
                event.abilityId,
              );
          }
          if (involved(event)) {
            const over = settings.combatTextKinds.overheal ? event.overheal : 0;
            const text = [
              event.amount > 0 && `+${event.amount}`,
              over > 0 && `(${over} overheal)`,
            ]
              .filter(Boolean)
              .join(' ');
            if (text)
              this.combatText(event.target, text, 'healing', 'heal', {
                periodic: ticking(event.auraId),
              });
          }
          break;
        case 'immune':
          if (involved(event))
            this.combatText(
              event.target,
              event.dr ? 'Immune (DR)' : 'Immune',
              'immune',
            );
          this.sfx.play('parry', root(event.target));
          break;
        case 'reflect':
          if (involved(event))
            this.combatText(event.source, 'Reflect', 'immune');
          this.sfx.play('parry', root(event.source));
          break;
        case 'grounded':
          if (involved(event))
            this.combatText(event.target, 'Grounded', 'immune');
          this.sfx.play('absorb', root(event.target));
          break;
        case 'cheatDeath':
          if (involved(event))
            this.combatText(event.target, AURAS[event.auraId].name, 'immune');
          break;
        case 'energize':
          if (involved(event))
            this.combatText(
              event.target,
              `+${Math.round(event.amount)} ${event.resource[0].toUpperCase()}${event.resource.slice(1)}`,
              'resources',
              'info',
              { color: `var(--${event.resource})` },
            );
          break;
        case 'reset':
          if (event.source === me)
            this.combatText(
              me,
              `${abilityDef(event.abilityId).name} reset`,
              'resets',
            );
          break;
        case 'revealed':
          if (involved(event))
            this.combatText(event.target, 'Revealed', 'dispels');
          break;
        case 'chain':
          this.fx.chain(event.source, event.target, event.abilityId);
          break;
        case 'sprung':
          this.fx.sprung(event.abilityId, event.point);
          break;
        case 'interrupt':
          if (event.target === me)
            this.lockoutDurations[event.school] = event.duration;
          if (involved(event))
            this.combatText(event.target, 'Interrupted', 'interrupts');
          this.fx.interrupted(event.target, event.school);
          this.sfx.play('shatter', root(event.target));
          break;
        case 'castStart':
          this.fx.castStart(
            event.source,
            event.target,
            event.abilityId,
            event.duration,
            event.point,
          );
          this.sfx.castStart(event.source, root(event.source), event.abilityId);
          break;
        case 'castStop':
          this.fx.castStop(event.source);
          // An interrupt plays its own sound.
          if (event.reason === 'interrupted') this.sfx.castStop(event.source);
          else this.sfx.fizzle(event.source, root(event.source));
          // A kick says so through its own `interrupt` event.
          if (
            event.source === me &&
            (event.reason === 'damage' || event.reason === 'cc')
          )
            this.combatText(me, 'Interrupted', 'interrupts');
          break;
        case 'castSuccess':
          this.views
            .get(event.source)
            ?.playOnce(castAnimation(abilityDef(event.abilityId)));
          this.fx.castSuccess(
            event.source,
            event.target,
            event.abilityId,
            event.travel,
            event.point,
          );
          this.sfx.castSuccess(
            event.source,
            root(event.source),
            event.abilityId,
          );
          break;
        case 'auraRemoved': {
          this.fx.auraRemoved(event.target, event.auraId, event.reason);
          if (event.auraId === 'ice-block' || event.auraId === 'freezing-trap')
            this.sfx.play('shatter', root(event.target));
          const def = auraDef(event.auraId);
          if (event.reason === 'dispelled') {
            if (involved(event))
              this.combatText(event.target, `${def.name} dispelled`, 'dispels');
            break;
          }
          // Dying strips every aura at once.
          if (
            event.target !== me ||
            events.some((e) => e.type === 'death' && e.target === me)
          )
            break;
          if (def.harmful && event.reason === 'broken')
            this.combatText(me, `${def.name} broken`, 'dispels');
          else if (!def.harmful) this.combatText(me, `-${def.name}`, 'auras');
          break;
        }
        case 'auraApplied': {
          const def = auraDef(event.auraId);
          // Zones reapply their auras every pulse; only a new aura gets text.
          const fresh = !had(event.target, event.auraId);
          if (fresh && event.target === me)
            this.combatText(
              me,
              def.harmful ? def.name : `+${def.name}`,
              'auras',
            );
          else if (
            fresh &&
            event.source === me &&
            def.harmful &&
            !def.exhaustion
          )
            this.combatText(event.target, def.name, 'debuffs');
          if (
            isAbilityId(event.auraId) &&
            event.source &&
            event.source !== event.target
          )
            this.sfx.impact(event.target, root(event.target), event.auraId);
          break;
        }
        case 'death':
          this.sfx.play('thud', root(event.target));
          if (
            event.killer === me &&
            event.target !== me &&
            !before?.units.find((u) => u.id === event.target)?.totem
          )
            this.combatText(event.target, 'Killing Blow', 'kills', 'kill');
          break;
        case 'error':
          hud.error = { text: event.message, at: performance.now() };
          break;
        default:
          break;
      }
    }
  }

  // -------------------------------------------------------------------------------------------
  // Frame
  // -------------------------------------------------------------------------------------------

  private frame = (ms: number): void => {
    if (!this.running) return;
    const cap = settings.fpsCap;
    // A millisecond early still counts, so a cap at the screen's own rate never drops a frame.
    if (cap > 0 && ms < this.nextDraw - 1) {
      requestAnimationFrame(this.frame);
      return;
    }
    if (cap > 0) this.nextDraw = Math.max(this.nextDraw + 1000 / cap, ms);
    if (settings.renderScale !== this.renderScale) {
      this.renderScale = settings.renderScale;
      this.resize();
    }
    const world = this.world!;
    const now = ms / 1000;
    const dt = Math.min(0.1, Math.max(0, now - this.lastFrame));
    this.lastFrame = now;

    if (!this.mouse.right) {
      const turn =
        (this.held('turnLeft') ? 1 : 0) - (this.held('turnRight') ? 1 : 0);
      this.charYaw += turn * KEYBOARD_TURN_RATE * dt;
      if (!this.mouse.left) this.rig.yaw += turn * KEYBOARD_TURN_RATE * dt;
    } else {
      this.charYaw = this.rig.yaw;
    }
    const follow = settings.cameraFollow;
    if (
      !this.mouse.left &&
      !this.mouse.right &&
      (follow === 'always' || (follow === 'moving' && this.moving))
    ) {
      const t = 1 - Math.exp(-CAMERA_FOLLOW_RATE * dt);
      this.rig.yaw = lerpAngle(this.rig.yaw, this.charYaw, t);
    }

    this.accumulator += dt;
    let steps = 0;
    while (this.accumulator >= DT && steps++ < MAX_STEPS_PER_FRAME) {
      this.stepLocal();
      this.accumulator -= DT;
    }
    if (this.accumulator > DT) this.accumulator = 0;
    this.visualOffset.multiplyScalar(Math.exp(-dt * CORRECTION_RATE));

    const renderTime =
      now +
      (this.clockOffset ?? 0) -
      (this.match.bracket === 'hub' ? HUB_INTERP_DELAY : INTERP_DELAY);
    const selfPos = this.updateSelf(dt);
    for (const [id, view] of this.views) {
      if (id === this.match.youId) continue;
      this.updateRemote(view, renderTime, dt);
    }
    if (selfPos) this.rig.update(selfPos, world, dt);
    this.sfx.listen(selfPos ?? this.camera.position, this.camera);
    this.critters?.update(dt);
    this.environment?.update(this.camera, selfPos ?? this.camera.position);
    const ambience = this.match.map.env?.ambience;
    if (ambience && this.environment)
      this.sfx.ambience(ambience, this.environment.night.value);
    music(this.match.bracket === 'hub' ? 'hub' : 'arena');
    this.updateEffects(now);
    this.fx.update(dt);
    this.updateReticle();
    this.updateNameplates();
    for (const floater of this.floaters) this.placeFloater(floater);

    this.hudTimer += dt;
    if (this.hudTimer >= HUD_INTERVAL) {
      this.hudTimer = 0;
      this.updateHud();
    }
    this.renderer.render(this.scene, this.camera);
    this.fps.frames++;
    if (ms - this.fps.since >= 1000) {
      hud.fps = Math.round((this.fps.frames * 1000) / (ms - this.fps.since));
      this.fps = { frames: 0, since: ms };
    }
    requestAnimationFrame(this.frame);
  };

  private stepLocal(): void {
    if (!this.predicted || !this.world) return;
    const input = this.sampleInput();
    this.previous = this.predicted;
    this.predicted = moveStep(this.world, this.predicted, input, this.control);
    this.moving = input.forward !== 0 || input.strafe !== 0;
    if (this.moving || input.jump) this.movedSeq = input.seq;
    this.pending.push(input);
    this.post({ t: 'input', ...input });
  }

  private held(binding: string): boolean {
    return settings.keys[binding]?.some((code) => this.keys.has(code)) ?? false;
  }

  private sampleInput(): MoveInput {
    const held = (binding: string) => (this.held(binding) ? 1 : 0);
    const forward =
      (held('forward') || (this.mouse.left && this.mouse.right) ? 1 : 0) -
      held('back');
    let strafe = held('strafeRight') - held('strafeLeft');
    if (this.mouse.right) strafe += held('turnRight') - held('turnLeft');
    return {
      seq: ++this.seq,
      forward,
      strafe: Math.max(-1, Math.min(1, strafe)),
      yaw: this.charYaw,
      jump: this.held('jump'),
    };
  }

  private updateSelf(dt: number): THREE.Vector3 | null {
    if (!this.predicted || !this.previous || !this.latest) return null;
    const view = this.views.get(this.match.youId);
    if (!view) return null;
    const alpha = this.accumulator / DT;
    const pos = toVector(this.previous.pos)
      .lerp(toVector(this.predicted.pos), alpha)
      .add(this.visualOffset);
    const forced =
      this.control.stunned ||
      this.control.fearDir !== null ||
      this.control.dash !== null;
    const snap = this.latest.units.find((u) => u.id === this.match.youId);
    view.update(dt, {
      pos,
      yaw: forced ? this.predicted.yaw : this.charYaw,
      vel: toVector(this.predicted.vel),
      grounded: this.predicted.grounded,
      dead: !!snap?.dead && !snap.ghost,
      casting: snap?.cast !== null && snap?.cast !== undefined,
      emote: this.emoteClip(this.match.youId, snap),
    });
    return pos;
  }

  private emoteClip(
    id: string,
    snap: UnitSnap | undefined,
  ): string | undefined {
    const stale =
      id === this.match.youId && (this.latest?.self?.ack ?? 0) < this.movedSeq;
    if (snap?.emote && !stale) return EMOTES[snap.emote].clip;
    const reaction = this.reactions.get(id);
    return reaction && performance.now() / 1000 < reaction.until
      ? reaction.clip
      : undefined;
  }

  private updateRemote(view: UnitView, renderTime: number, dt: number): void {
    const list = this.samples.get(view.id);
    const snap = this.latest?.units.find((u) => u.id === view.id);
    if (!list || list.length === 0 || !snap) return;
    let a = list[0];
    let b = list[list.length - 1];
    for (let i = 0; i < list.length - 1; i++) {
      if (list[i].t <= renderTime && list[i + 1].t >= renderTime) {
        a = list[i];
        b = list[i + 1];
        break;
      }
    }
    const span = b.t - a.t;
    const t =
      span > 0 ? Math.min(1, Math.max(0, (renderTime - a.t) / span)) : 1;
    view.update(dt, {
      pos: a.pos.clone().lerp(b.pos, t),
      yaw: lerpAngle(a.yaw, b.yaw, t),
      vel: b.vel,
      grounded: b.grounded,
      dead: snap.dead && !snap.ghost,
      casting: snap.cast !== null,
      emote: this.emoteClip(view.id, snap),
    });
  }

  private viewFor(unit: UnitSnap): UnitView {
    let view = this.views.get(unit.id);
    const classId = unit.classes[unit.active];
    // Swapping class (or picking another in the hub) swaps the model.
    if (view && view.classId !== classId) {
      view.dispose();
      this.scene.remove(view.root);
      view = undefined;
    }
    if (!view) {
      const npc = HUB_NPCS.find((n) => n.id === unit.id);
      const model = unit.totem
        ? TOTEM_MODEL
        : (npc?.model ?? CLASS_LOOKS[classId].character);
      view = new UnitView(
        unit.id,
        classId,
        unit.name,
        unit.team === this.myTeam,
        unit.id === this.match.youId,
        this.characters[model],
        this.items,
        this.overlay,
        npc || unit.totem ? NO_HANDS : undefined,
        this.characters,
      );
      if (npc) view.nameplate.classList.add('npc');
      if (unit.totem) view.makeTotem(TOTEMS[unit.totem] ?? 0xffffff);
      const created = view;
      view.onFootfall = (step) => this.footfall(created, step);
      view.setTargeted(this.targetId === unit.id);
      this.views.set(unit.id, view);
      this.scene.add(view.root);
    }
    view.setTag(unit.tag);
    const worn = app.roster?.flat().find((f) => f.id === unit.id);
    view.setTrails(unit.trail ?? worn?.trail, unit.offTrail ?? worn?.offTrail);
    view.setStealthed(unit.stealthed || !!unit.ghost);
    view.setAuras(unit.auras);
    view.setHealth(unit.hp / unit.maxHp);
    return view;
  }

  private updateNameplates(): void {
    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    const since = performance.now() / 1000 - this.latestAt;
    // Before the nameplate writes below, so rayAt's getBoundingClientRect doesn't force a layout.
    const ray = this.hoverAt && this.rayAt(this.hoverAt.x, this.hoverAt.y);
    const cursor = this.placing
      ? 'crosshair'
      : ray && this.pick(ray)
        ? 'pointer'
        : '';
    if (this.canvas.style.cursor !== cursor) this.canvas.style.cursor = cursor;
    const projected = new THREE.Vector3();
    for (const [id, view] of this.views) {
      if (!view.root.visible) {
        view.bubble.style.display = 'none';
        continue;
      }
      projected
        .copy(view.root.position)
        .setY(view.root.position.y + NAMEPLATE_HEIGHT + 0.75);
      projected.project(this.camera);
      const behind = projected.z > 1;
      const at = `translate(${((projected.x + 1) / 2) * width}px, ${((1 - projected.y) / 2) * height}px) translate(-50%, -100%)`;
      view.nameplate.style.display =
        behind || id === this.match.youId ? 'none' : '';
      view.nameplate.style.transform = at;
      view.bubble.style.display = behind || !view.speaking ? 'none' : '';
      view.bubble.style.transform = `${at} translateY(-46px)`;
      const cast = this.latest?.units.find((u) => u.id === id)?.cast;
      view.setCast(cast ? castProgress(cast, since) : null);
    }
    this.critters?.label(ray, this.camera, width, height);
  }

  // -------------------------------------------------------------------------------------------
  // Effects and combat text
  // -------------------------------------------------------------------------------------------

  /** Swaps in the open collision and sinks the gates into the floor. */
  private openGates(): void {
    if (!this.openWorld) return;
    this.world?.free();
    this.world = this.openWorld;
    this.openWorld = null;
    const now = performance.now() / 1000;
    for (const gate of this.gates) {
      this.sfx.play('gate', gate);
      const top = gate.position.y;
      this.effects.push({
        object: gate,
        start: now,
        end: now + GATE_OPEN_SECONDS,
        update: (p) => {
          gate.position.y = top - p * TILE_SIZE;
          gate.visible = p < 1;
        },
      });
    }
  }

  private footfall(view: UnitView, step: Footfall): void {
    if (!view.root.visible) return;
    const surface = this.surfaceAt(view.root.position);
    this.sfx.footfall(view.root, surface, step, view.classId);
  }

  private surfaceAt(feet: THREE.Vector3): Surface {
    if (!this.mapObject) return 'stone';
    this.ground.ray.origin.copy(feet).setY(feet.y + 0.5);
    const hit = this.ground.intersectObject(this.mapObject)[0];
    if (!hit) return 'stone';
    const { terrain, piece } = hit.object.userData;
    return surfaceOf(terrain ? null : piece, this.match.map.env?.terrain);
  }

  private updateEffects(now: number): void {
    this.effects = this.effects.filter((fx) => {
      const progress = Math.min(
        1,
        (now - fx.start) / Math.max(1e-3, fx.end - fx.start),
      );
      fx.update(progress);
      if (progress < 1) return true;
      fx.object.removeFromParent();
      return false;
    });
  }

  private combatText(
    unitId: string,
    text: string,
    kind: CombatTextKind,
    style:
      | 'spell'
      | 'melee'
      | 'taken'
      | 'heal'
      | 'absorb'
      | 'kill'
      | 'info' = 'info',
    { periodic = false, color }: { periodic?: boolean; color?: string } = {},
  ): void {
    const shown = settings.combatTextKinds;
    const view = this.views.get(unitId);
    if (!view || !settings.combatText || !shown[kind]) return;
    if (periodic && !shown.periodic) return;
    const el = document.createElement('div');
    el.className = `combat-text ${style}${periodic ? ' periodic' : ''}`;
    el.textContent = text;
    if (color) el.style.color = color;
    const floater = {
      el,
      unitId,
      anchor: new THREE.Vector3(),
      jitter: (Math.random() - 0.5) * 40,
    };
    this.floaters.push(floater);
    this.placeFloater(floater);
    this.overlay.appendChild(el);
    setTimeout(() => {
      el.remove();
      this.floaters = this.floaters.filter((f) => f !== floater);
    }, 1300);
  }

  /** Over its unit wherever the camera is; where the unit was last seen once its view is gone. */
  private placeFloater(floater: (typeof this.floaters)[number]): void {
    const view = this.views.get(floater.unitId);
    if (view)
      floater.anchor.copy(view.root.position).setY(view.root.position.y + 2);
    const projected = floater.anchor.clone().project(this.camera);
    floater.el.style.display = projected.z > 1 ? 'none' : '';
    floater.el.style.left = `${((projected.x + 1) / 2) * this.canvas.clientWidth + floater.jitter}px`;
    floater.el.style.top = `${((1 - projected.y) / 2) * this.canvas.clientHeight}px`;
  }

  // -------------------------------------------------------------------------------------------
  // Input
  // -------------------------------------------------------------------------------------------

  private bindInput(): void {
    const listen = <K extends keyof WindowEventMap>(
      target: Window | HTMLElement,
      type: K,
      handler: (e: WindowEventMap[K]) => void,
      options?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, handler as EventListener, options);
      this.disposers.push(() =>
        target.removeEventListener(type, handler as EventListener),
      );
    };

    listen(window, 'pointerdown', () => this.sfx.resume());
    listen(window, 'keydown', (e) => {
      this.sfx.resume();
      if (e.target instanceof HTMLInputElement) return;
      this.keys.add(e.code);
      if (e.code === 'Escape' && !e.repeat) {
        const casting = this.latest?.units.find(
          (u) => u.id === this.match.youId,
        )?.cast;
        if (this.placing) this.stopPlacing();
        else if (casting) this.post({ t: 'stopCast' });
        else if (hud.npc) {
          hud.npc = null;
          click();
        } else if (this.targetId) this.setTarget(null);
        else hud.settingsOpen = true;
        return;
      }
      const binding = boundTo(chord(e)) ?? boundTo(e.code);
      if (!binding) return;
      e.preventDefault();
      if (e.repeat) return;
      if (binding === 'swap') return this.cast('swap');
      if (binding.startsWith('action'))
        return this.useAction(Number(binding.slice('action'.length)) - 1);
      if (binding === 'spellbook' || binding === 'talents') {
        click();
        return togglePanel(binding);
      }
      if (
        binding === 'classes' ||
        binding === 'leaderboard' ||
        binding === 'achievements' ||
        binding === 'character'
      ) {
        if (this.match.bracket !== 'hub') return;
        click();
        return togglePanel(binding);
      }
      if (binding === 'nextTarget') return this.cycleTarget();
      if (binding === 'targetSelf') return this.setTarget(this.match.youId);
      if (binding === 'targetParty1' || binding === 'targetParty2') {
        const mates =
          this.latest?.units.filter(
            (u) =>
              u.team === this.myTeam && u.id !== this.match.youId && !u.totem,
          ) ?? [];
        const mate = mates[binding === 'targetParty1' ? 0 : 1];
        if (mate) this.setTarget(mate.id);
      }
    });
    listen(window, 'keyup', (e) => this.keys.delete(e.code));
    listen(window, 'blur', () => {
      this.keys.clear();
      this.mouse.left = this.mouse.right = false;
    });

    listen(this.canvas, 'mousedown', (e) => {
      // Under pointer lock clientX/Y are frozen, so restarting a drag would never cross the threshold.
      if (!this.mouse.left && !this.mouse.right) {
        this.mouse.downX = e.clientX;
        this.mouse.downY = e.clientY;
        this.mouse.dragging = false;
      }
      if (e.button === 0) this.mouse.left = true;
      if (e.button === 2) this.mouse.right = true;
    });
    listen(window, 'mousemove', (e) => {
      this.hoverAt =
        e.target === this.canvas && !this.mouse.dragging
          ? { x: e.clientX, y: e.clientY }
          : null;
      if (!this.mouse.left && !this.mouse.right) return;
      if (!this.mouse.dragging) {
        if (
          Math.hypot(
            e.clientX - this.mouse.downX,
            e.clientY - this.mouse.downY,
          ) < 4
        )
          return;
        this.mouse.dragging = true;
        void this.canvas.requestPointerLock()?.catch?.(() => {});
      }
      this.rig.rotate(e.movementX, e.movementY);
    });
    listen(window, 'mouseup', (e) => {
      const wasDragging = this.mouse.dragging;
      if (e.button === 0) this.mouse.left = false;
      if (e.button === 2) this.mouse.right = false;
      if (!this.mouse.left && !this.mouse.right) {
        this.mouse.dragging = false;
        if (document.pointerLockElement) document.exitPointerLock();
      }
      if (wasDragging || e.target !== this.canvas) return;
      if (this.placing) {
        if (e.button === 0) this.place(e.clientX, e.clientY);
        else this.stopPlacing();
        return;
      }
      const hit = this.pick(this.rayAt(e.clientX, e.clientY));
      if (e.button === 0) this.setTarget(hit);
      if (e.button === 2) this.interact(hit);
    });
    listen(this.canvas, 'contextmenu', (e) => e.preventDefault());
    listen(this.canvas, 'wheel', (e) => this.rig.zoom(e.deltaY), {
      passive: true,
    });
  }

  private rayAt(clientX: number, clientY: number): THREE.Raycaster {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    return this.raycaster;
  }

  private pick(ray: THREE.Raycaster): string | null {
    const boxes = [...this.views.values()]
      .filter((v) => v.root.visible)
      .map((v) => v.hitbox);
    const hit = ray.intersectObjects(boxes, false)[0];
    return (hit?.object.userData.unitId as string | undefined) ?? null;
  }

  /** Right click: targets a unit, talks to an NPC in range, and auto-attacks an enemy. Empty ground keeps the target. */
  private interact(id: string | null): void {
    if (!id) return;
    this.setTarget(id);
    if (HUB_NPCS.some((n) => n.id === id)) {
      const inRange = (hud.target?.distance ?? Infinity) <= NPC_RANGE;
      this.views.get(id)?.playOnce(inRange ? 'emote-yes' : 'emote-no');
      if (inRange) hud.npc = id;
      else
        hud.error = {
          text: 'You are too far away to interact.',
          at: performance.now(),
        };
      return;
    }
    const unit = this.latest?.units.find((u) => u.id === id);
    if (unit && unit.team !== this.myTeam)
      this.post({ t: 'attack', targetId: id });
  }

  setTarget(id: string | null): void {
    if (id === this.targetId) return;
    this.views.get(this.targetId ?? '')?.setTargeted(false);
    this.targetId = id;
    this.views.get(id ?? '')?.setTargeted(true);
    this.post({ t: 'target', targetId: id });
    this.updateHud();
  }

  /** Tab: nearest living enemies in front of the camera first, cycling on repeat presses. */
  private cycleTarget(): void {
    const me = this.views.get(this.match.youId);
    if (!me || !this.latest) return;
    const forward = new THREE.Vector3();
    this.camera.getWorldDirection(forward);
    const candidates = this.latest.units
      .filter((u) => u.team !== this.myTeam && !u.dead)
      .map((u) => {
        const to = toVector(u.pos).sub(me.root.position);
        const behind = to.clone().setY(0).dot(forward.clone().setY(0)) < 0;
        // Players before totems, then nearest in front first.
        const score = to.length() + (behind ? 1000 : 0) + (u.totem ? 10000 : 0);
        return { id: u.id, score };
      })
      .sort((a, b) => a.score - b.score)
      .map((c) => c.id);
    if (candidates.length === 0) return;
    const sameSet =
      candidates.length === this.tabCycle.length &&
      candidates.every((id) => this.tabCycle.includes(id));
    if (!sameSet) this.tabCycle = candidates;
    const index = this.targetId ? this.tabCycle.indexOf(this.targetId) : -1;
    this.setTarget(this.tabCycle[(index + 1) % this.tabCycle.length]);
  }

  private useAction(slot: number): void {
    const abilityId = hud.actions[slot]?.abilityId;
    if (abilityId) this.cast(abilityId);
  }

  /** Keybinds and clicks on the action bar. */
  cast(abilityId: AbilityId): void {
    if (this.match.bracket === 'hub') {
      hud.error = {
        text: "You can't use abilities in the hub.",
        at: performance.now(),
      };
      return;
    }
    this.stopPlacing();
    const ability = abilityDef(abilityId);
    if (ability.target !== 'ground')
      return this.post({ t: 'cast', abilityId, targetId: this.targetId });
    const reticle = areaDecal(areaRadius(ability), 0xffffff);
    reticle.visible = false;
    this.scene.add(reticle);
    this.placing = { abilityId, reticle };
  }

  private place(clientX: number, clientY: number): void {
    const point = this.floorAt(clientX, clientY);
    if (!this.placing) return;
    if (!point) {
      hud.error = { text: 'Invalid location.', at: performance.now() };
      return;
    }
    this.post({
      t: 'cast',
      abilityId: this.placing.abilityId,
      targetId: null,
      point: [point.x, point.y, point.z],
    });
    this.stopPlacing();
  }

  private stopPlacing(): void {
    if (!this.placing) return;
    this.scene.remove(this.placing.reticle);
    disposeObject(this.placing.reticle);
    this.placing = null;
  }

  private updateReticle(): void {
    if (!this.placing) return;
    const point = this.hoverAt && this.floorAt(this.hoverAt.x, this.hoverAt.y);
    this.placing.reticle.visible = !!point;
    if (point) this.placing.reticle.position.copy(point);
  }

  /** The walkable surface under the cursor, from the same collision the server snaps ground targets to. */
  private floorAt(clientX: number, clientY: number): THREE.Vector3 | null {
    const { origin, direction } = this.rayAt(clientX, clientY).ray;
    const hit = this.world?.castRay(origin, direction, 200);
    if (!hit || hit.normal.y < 0.5) return null;
    return origin.clone().addScaledVector(direction, hit.distance);
  }

  cancelAura(auraId: AuraId): void {
    this.post({ t: 'cancelAura', auraId });
  }

  release(): void {
    this.post({ t: 'release' });
  }

  private resize(): void {
    const parent = this.canvas.parentElement!;
    this.renderer.setPixelRatio(pixelRatio(this.renderScale));
    this.renderer.setSize(parent.clientWidth, parent.clientHeight, false);
    this.camera.aspect = parent.clientWidth / Math.max(1, parent.clientHeight);
    this.camera.updateProjectionMatrix();
  }

  // -------------------------------------------------------------------------------------------
  // HUD
  // -------------------------------------------------------------------------------------------

  private updateHud(): void {
    const snap = this.latest;
    if (!snap) return;
    const since = performance.now() / 1000 - this.latestAt;
    const me = snap.units.find((u) => u.id === this.match.youId);
    const target = this.targetId
      ? snap.units.find((u) => u.id === this.targetId)
      : undefined;
    const myPos = this.views.get(this.match.youId)?.root.position;
    const distanceTo = (unit: UnitSnap) => {
      const view = this.views.get(unit.id);
      if (!myPos || !view || unit.id === this.match.youId) return null;
      return Math.max(
        0,
        view.root.position.distanceTo(myPos) - 2 * UNIT_RADIUS,
      );
    };
    const frame = (unit: UnitSnap, hidden = false): FrameView => ({
      id: unit.id,
      name: unit.name,
      classId: unit.classes[unit.active],
      other:
        unit.dummy || unit.classes[0] === unit.classes[1]
          ? null
          : unit.classes[1 - unit.active],
      friendly: unit.team === this.myTeam,
      hp: unit.hp,
      maxHp: unit.maxHp,
      pools: [unit.active, 1 - unit.active].map((i) => ({
        type: CLASSES[unit.classes[i]].resource,
        value: unit.resources[i],
        max: CLASSES[unit.classes[i]].maxResource,
      })),
      dead: unit.dead,
      ghost: !!unit.ghost,
      stealthed: unit.stealthed,
      hidden,
      cast:
        unit.cast && !hidden
          ? {
              name: abilityDef(unit.cast.abilityId).name,
              progress: castProgress(unit.cast, since),
              remaining: Math.max(
                0,
                unit.cast.duration - unit.cast.elapsed - since,
              ),
              channel: unit.cast.channel,
            }
          : null,
      auras: hidden
        ? []
        : unit.auras.map((a): AuraView => {
            const def = auraDef(a.auraId);
            return {
              auraId: a.auraId,
              name: def.name,
              icon: def.icon ?? a.auraId,
              description: def.description,
              harmful: def.harmful,
              dispel: def.dispel ?? null,
              absorb: a.absorb ?? 0,
              cancelable: unit.id === this.match.youId && cancelableAura(def),
              remaining: a.remaining > 0 ? Math.max(0, a.remaining - since) : 0,
              duration: a.duration,
            };
          }),
      distance: hidden ? null : distanceTo(unit),
    });

    hud.phase = snap.phase;
    hud.phaseRemaining = Math.max(0, snap.phaseRemaining - since);
    hud.dampening = snap.dampening;
    hud.player = me ? frame(me) : null;
    hud.target = target ? frame(target) : null;
    const targetTarget = target?.targetId
      ? snap.units.find((u) => u.id === target.targetId)
      : undefined;
    hud.targetTarget = targetTarget ? frame(targetTarget) : null;
    const npc = snap.units.find((u) => u.id === hud.npc);
    if (hud.npc && ((npc && distanceTo(npc)) ?? Infinity) > NPC_RANGE)
      hud.npc = null;
    hud.team = snap.units
      .filter(
        (u) => u.team === this.myTeam && u.id !== this.match.youId && !u.totem,
      )
      .map((u) => frame(u));
    const visible = new Set(snap.units.map((u) => u.id));
    hud.enemies = [...this.knownEnemies.values()].map((u) =>
      frame(
        visible.has(u.id) ? snap.units.find((s) => s.id === u.id)! : u,
        !visible.has(u.id),
      ),
    );

    const self = snap.self;
    hud.lossOfControl =
      me && self && hud.player
        ? lossOfControl(
            hud.player.auras,
            (Object.entries(self.lockouts) as [School, number][]).map(
              ([school, remaining]) => ({
                school,
                remaining: remaining - since,
                duration: this.lockoutDurations[school] ?? 0,
              }),
            ),
            (school) =>
              [...spellbook(me.classes[0]), ...spellbook(me.classes[1])].find(
                (id) => abilityDef(id).school === school,
              ) ?? me.classes[me.active],
          )
        : null;
    const swing = self?.swing;
    const swingLeft = swing ? Math.max(0, swing.remaining - since) : 0;
    hud.swing = swing
      ? {
          progress: 1 - swingLeft / Math.max(1e-3, swing.duration),
          remaining: swingLeft,
        }
      : null;
    if (!me || !self) return;
    const active = me.classes[me.active];
    const other = me.classes[1 - me.active];
    hud.meter = {
      damage: meterRows(this.meter.damage, active),
      healing: meterRows(this.meter.healing, active),
      seconds: this.meter.seconds,
    };
    hud.combo = self.combo;
    hud.showCombo = me.classes.includes('rogue');
    const gcdLeft = Math.max(0, self.gcd.remaining - since);
    const targetDistance = target ? distanceTo(target) : null;
    const disciplines = me.classes.map((c) => talentsFor(c)[2]);
    /** The other class's row shows only its cooldowns: the GCD is the playing class's. */
    const action = (
      abilityId: AbilityId,
      binding: string,
      pool: number,
      playing = true,
    ): ActionView => {
      const ability = abilityDef(abilityId);
      const cd = self.cooldowns[abilityId];
      let cooldown = cd ? Math.max(0, cd.remaining - since) : 0;
      let cooldownDuration = cd?.duration ?? 0;
      const gcd = ability.gcd ?? CLASSES[active].gcd;
      if (playing && gcd > 0 && gcdLeft > cooldown) {
        cooldown = gcdLeft;
        cooldownDuration = self.gcd.duration;
      }
      const range = rangeOf(abilityId, disciplines);
      const stealthFree = me.auras.some((a) => {
        const next = auraDef(a.auraId).next;
        return next?.stealthed && next.abilities.includes(abilityId);
      });
      return {
        abilityId,
        name: ability.name,
        icon: abilityId,
        key: keyLabel(settings.keys[binding]?.[0], true),
        cooldown,
        cooldownDuration,
        noResource: me.resources[pool] < costOf(abilityId, disciplines),
        uses: ability.uses ? ability.uses - (self.used[abilityId] ?? 0) : null,
        outOfRange:
          ability.target === 'enemy' &&
          target !== undefined &&
          targetDistance !== null &&
          targetDistance > range,
        unusable:
          (!!ability.requiresStealth && !me.stealthed && !stealthFree) ||
          (!!ability.finisher && self.combo === 0) ||
          (self.lockouts[ability.school] ?? 0) > since ||
          (ability.executeBelow !== undefined &&
            target !== undefined &&
            target.hp / target.maxHp > ability.executeBelow),
        queued: self.queued === abilityId,
        active:
          !!ability.cancelable &&
          ability.effects.some(
            (e) =>
              e.type === 'aura' &&
              e.self &&
              me.auras.some((a) => a.auraId === e.aura),
          ),
      };
    };
    hud.actions = [...barFor(active), ...utilityBar()].map((id, i) =>
      action(id, `action${i + 1}`, me.active),
    );
    hud.offActions =
      other === active
        ? []
        : barFor(other).map((id, i) =>
            action(id, `action${i + 1}`, 1 - me.active, false),
          );
    hud.swap =
      other === active
        ? null
        : {
            ...action('swap', 'swap', me.active),
            name: `Swap to ${CLASSES[other].name}`,
          };
  }
}

function castProgress(
  cast: { elapsed: number; duration: number; channel: boolean },
  since: number,
): number {
  const progress = Math.min(
    1,
    (cast.elapsed + since) / Math.max(1e-3, cast.duration),
  );
  return cast.channel ? 1 - progress : progress;
}
